import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';

const TodoModal = ({ todo, categories, tags, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    categoryId: '',
    status: 'pending',
    priority: 'medium',
    timeframe: '',
    dueDate: '',
    tags: [],
    estimatedTime: '',
    subtasks: []
  });

  const [newSubtask, setNewSubtask] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);

  useEffect(() => {
    if (todo) {
      setFormData({
        title: todo.title || '',
        description: todo.description || '',
        categoryId: todo.categoryId || '',
        status: todo.status || 'pending',
        priority: todo.priority || 'medium',
        timeframe: todo.timeframe || '',
        dueDate: todo.dueDate ? todo.dueDate.split('T')[0] : '',
        tags: todo.tags || [],
        estimatedTime: todo.estimatedTime || '',
        subtasks: todo.subtasks || []
      });
      setSelectedTags(todo.tags || []);
    }
  }, [todo]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddSubtask = () => {
    if (!newSubtask.trim()) return;
    
    setFormData(prev => ({
      ...prev,
      subtasks: [...prev.subtasks, { id: Date.now().toString(), title: newSubtask, completed: false }]
    }));
    setNewSubtask('');
  };

  const handleToggleSubtask = (id) => {
    setFormData(prev => ({
      ...prev,
      subtasks: prev.subtasks.map(st => 
        st.id === id ? { ...st, completed: !st.completed } : st
      )
    }));
  };

  const handleRemoveSubtask = (id) => {
    setFormData(prev => ({
      ...prev,
      subtasks: prev.subtasks.filter(st => st.id !== id)
    }));
  };

  const handleToggleTag = (tagId) => {
    setSelectedTags(prev => {
      const newTags = prev.includes(tagId)
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId];
      setFormData(fd => ({ ...fd, tags: newTags }));
      return newTags;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const dataToSave = {
      ...formData,
      tags: selectedTags,
      estimatedTime: formData.estimatedTime ? parseInt(formData.estimatedTime) : null
    };

    onSave(dataToSave);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{todo ? 'Edit Todo' : 'Create New Todo'}</h2>
          <button className="modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              placeholder="What needs to be done?"
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              placeholder="Add more details..."
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Category</label>
              <select name="categoryId" value={formData.categoryId} onChange={handleChange}>
                <option value="">Select category</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Priority</label>
              <select name="priority" value={formData.priority} onChange={handleChange}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Status</label>
              <select name="status" value={formData.status} onChange={handleChange}>
                <option value="pending">Pending</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            <div className="form-group">
              <label>Timeframe</label>
              <select name="timeframe" value={formData.timeframe} onChange={handleChange}>
                <option value="">None</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="quarter">This Quarter</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Due Date</label>
              <input
                type="date"
                name="dueDate"
                value={formData.dueDate}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Estimated Time (hour)</label>
              <input
                type="number"
                name="estimatedTime"
                value={formData.estimatedTime}
                onChange={handleChange}
                placeholder="60"
                min="0"
              />
            </div>
          </div>

          {tags.length > 0 && (
            <div className="form-group">
              <label>Tags</label>
              <div className="tags-selector">
                {tags.map(tag => (
                  <button
                    key={tag.id}
                    type="button"
                    className={`tag-chip ${selectedTags.includes(tag.id) ? 'selected' : ''}`}
                    style={{ 
                      backgroundColor: selectedTags.includes(tag.id) ? tag.color : `${tag.color}20`,
                      color: selectedTags.includes(tag.id) ? '#fff' : tag.color
                    }}
                    onClick={() => handleToggleTag(tag.id)}
                  >
                    {tag.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="form-group">
            <label>Subtasks</label>
            <div className="subtasks-input">
              <input
                type="text"
                value={newSubtask}
                onChange={(e) => setNewSubtask(e.target.value)}
                placeholder="Add a subtask..."
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSubtask())}
              />
              <button type="button" onClick={handleAddSubtask} className="btn-icon">
                <Plus size={20} />
              </button>
            </div>
            {formData.subtasks.length > 0 && (
              <div className="subtasks-list">
                {formData.subtasks.map(subtask => (
                  <div key={subtask.id} className="subtask-item">
                    <div className="subtask-content">
                      <input
                        type="checkbox"
                        checked={subtask.completed}
                        onChange={() => handleToggleSubtask(subtask.id)}
                        className="subtask-checkbox-input"
                      />
                      <span className={subtask.completed ? 'completed' : ''}>
                        {subtask.title}
                      </span>
                    </div>
                    <button 
                      type="button" 
                      onClick={() => handleRemoveSubtask(subtask.id)}
                      className="btn-icon-small"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              {todo ? 'Update' : 'Create'} Todo
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TodoModal;
